import React from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { LoadingBlock, ErrorState } from "@/components/ds";
import { MfaRecoveryCard } from "@/components/security/MfaRecoveryCard";

const STATE_TONE: Record<string, string> = {
  payment_received: "bg-emerald-100 text-emerald-700", onboarding: "bg-blue-100 text-blue-700",
  ready_for_qa: "bg-purple-100 text-purple-700", ready_for_access: "bg-teal-100 text-teal-700",
  active: "bg-emerald-100 text-emerald-700", past_due: "bg-orange-100 text-orange-700",
  suspended: "bg-red-100 text-red-700", cancelled: "bg-red-100 text-red-700",
};
const label = (s: string) => s.replace(/_/g, " ");

export default function InternalOverview() {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["internal-overview"], queryFn: api.internalOverview });
  // Only platform/senior admins may reset two-factor; the API refuses anyone else regardless.
  const { data: me } = useQuery({ queryKey: ["internal-me"], queryFn: api.internalMe, retry: false });
  if (isLoading) return <LoadingBlock rows={5} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;

  const c = data.counts;
  const cards = [
    ["Paid — awaiting setup", c.payment_received], ["Onboarding", c.onboarding],
    ["Ready for QA", c.ready_for_qa], ["Ready for access", c.ready_for_access],
    ["Active customers", c.active], ["Past due", c.past_due],
    ["Cancelled", c.cancelled], ["Total organizations", data.total_organizations],
  ];

  return (
    <div className="space-y-6" data-testid="internal-overview">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Operations Overview</h1>
        <p className="text-sm text-slate-500">Platform-wide customer lifecycle at a glance.</p>
      </div>

      {data.needing_attention > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-sm text-orange-800" data-testid="internal-attention">
          <AlertTriangle className="h-4 w-4" /> {data.needing_attention} customer(s) need attention (past-due or awaiting QA/access).
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="internal-counts">
        {cards.map(([lbl, val]) => (
          <div key={lbl as string} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-slate-500">{lbl}</p>
            <p className="mt-1 font-display text-3xl font-semibold text-slate-900">{val ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="internal-aging">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3 text-sm font-semibold text-slate-800">
          <span>Onboarding aging — waiting longest</span>
          <span className="text-xs font-normal text-slate-400">{data.unassigned} unassigned</span>
        </div>
        {data.onboarding_aging.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-slate-400">No customers currently in onboarding.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
              <th className="px-5 py-2">Business</th><th className="px-5 py-2">Status</th>
              <th className="px-5 py-2">Since</th><th className="px-5 py-2">Assigned</th></tr></thead>
            <tbody>
              {data.onboarding_aging.map((o: any) => (
                <tr key={o.id} className="border-b border-slate-50" data-testid={`internal-aging-${o.id}`}>
                  <td className="px-5 py-2 font-medium text-slate-800">{o.name || o.id}</td>
                  <td className="px-5 py-2"><span className={`rounded-full px-2 py-0.5 text-xs ${STATE_TONE[o.status] || "bg-slate-100 text-slate-600"}`}>{label(o.status || "—")}</span></td>
                  <td className="px-5 py-2 text-slate-500">{String(o.created_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-2">{o.assigned ? <span className="text-emerald-600">Yes</span> : <span className="text-slate-400">Unassigned</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {me?.is_platform_admin && <MfaRecoveryCard />}
    </div>
  );
}
